// Slash-command sub-command / argument suggestions from each command's `autocomplete_data` tree
// (shared/API.md §36.1–§36.3). A port of the server's own walk — `App.getSuggestions` /
// `parseArguments` / `parseListItems` in mattermost/server/channels/app/command_autocomplete.go —
// so the strip a reader sees locally is the list `GET …/commands/autocomplete_suggestions` would
// answer, without the round trip. Only a DynamicList argument needs the server (the plugin owns
// its items); the walk reports that with `needsServer` and the caller asks
// `autocomplete_suggestions` for the whole line (§36.3).
//
// Twin: windows/src/SatoriChat.Core/Logic/CommandAutocomplete.cs (same cases in
// CommandAutocompleteTests.cs).

/** One `AutocompleteListItem` of a StaticList (or of a DynamicList's server answer). */
export interface AutocompleteListItem {
  item: string;
  hint: string;
  helpText: string;
}

/** 'Unknown' = a type this client does not know: it suggests nothing and consumes one word (§36.1). */
export type AutocompleteArgType = 'TextInput' | 'StaticList' | 'DynamicList' | 'Unknown';

/** One `AutocompleteArg`. `name` non-empty = a named `--name value` argument. */
export interface AutocompleteArg {
  name: string;
  helpText: string;
  type: AutocompleteArgType;
  required: boolean;
  /** TextInput: the placeholder the strip shows while the value is typed. */
  hint: string;
  /** StaticList: the choices. */
  items: AutocompleteListItem[];
}

/** One node of the tree (`model.AutocompleteData`): a command or a sub-command. */
export interface AutocompleteNode {
  trigger: string;
  hint: string;
  helpText: string;
  /** `system_user` (everyone) or `system_admin`; '' = admins only (the server's filter, ported exactly). */
  roleId: string;
  arguments: AutocompleteArg[];
  subCommands: AutocompleteNode[];
}

/**
 * One strip row (`model.AutocompleteSuggestion`). `complete` is the whole line WITHOUT the leading
 * "/" once this row is picked; `suggestion` is the word the row offers — '' marks an ARGUMENT HINT
 * row (what to type next), which is drawn but can never be picked.
 */
export interface CommandSuggestion {
  complete: string;
  suggestion: string;
  hint: string;
  description: string;
}

export interface CommandSuggestions {
  rows: CommandSuggestion[];
  /** The walk reached a DynamicList argument: only the server can list its values (§36.3). */
  needsServer: boolean;
}

// MARK: - Decoding (the server serialises AutocompleteData with Go field names: `Trigger`, `HelpText`…)

function rec(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function pick(o: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
}

function s(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function listItem(raw: unknown): AutocompleteListItem | null {
  const o = rec(raw);
  const item = s(pick(o, 'Item', 'item'));
  if (item === '') return null;
  return { item, hint: s(pick(o, 'Hint', 'hint')), helpText: s(pick(o, 'HelpText', 'helptext', 'help_text')) };
}

function argument(raw: unknown): AutocompleteArg | null {
  const o = rec(raw);
  const kind = s(pick(o, 'Type', 'type'));
  const type: AutocompleteArgType = kind === 'TextInput' || kind === 'StaticList' || kind === 'DynamicList' ? kind : 'Unknown';
  const data = rec(pick(o, 'Data', 'data'));
  return {
    name: s(pick(o, 'Name', 'name')),
    helpText: s(pick(o, 'HelpText', 'helptext', 'help_text')),
    type,
    required: pick(o, 'Required', 'required') === true,
    hint: s(pick(data, 'Hint', 'hint')),
    items: arr(pick(data, 'PossibleArguments', 'possible_arguments'))
      .map(listItem)
      .filter((x): x is AutocompleteListItem => x !== null),
  };
}

/** One tree node, tolerant (`null` and `[]` both mean none). */
export function decodeNode(raw: unknown): AutocompleteNode | null {
  const o = rec(raw);
  const trigger = s(pick(o, 'Trigger', 'trigger'));
  if (trigger === '') return null;
  return {
    trigger,
    hint: s(pick(o, 'Hint', 'hint')),
    helpText: s(pick(o, 'HelpText', 'helptext', 'help_text')),
    roleId: s(pick(o, 'RoleID', 'role_id', 'roleId')),
    arguments: arr(pick(o, 'Arguments', 'arguments'))
      .map(argument)
      .filter((x): x is AutocompleteArg => x !== null),
    subCommands: arr(pick(o, 'SubCommands', 'sub_commands', 'subCommands'))
      .map(decodeNode)
      .filter((x): x is AutocompleteNode => x !== null),
  };
}

/**
 * A command of `GET /teams/{id}/commands/autocomplete` → its tree. A command without
 * `autocomplete_data` gets the server's own stand-in (`model.NewAutocompleteData(trigger, hint,
 * desc)`): no arguments, no sub-commands — the strip then ends at its space, as before.
 */
export function nodeForCommand(raw: unknown): AutocompleteNode | null {
  const o = rec(raw);
  const trigger = s(o.trigger);
  if (trigger === '') return null;
  const data = decodeNode(o.autocomplete_data);
  if (data) return { ...data, trigger };
  return { trigger, hint: s(o.auto_complete_hint), helpText: s(o.auto_complete_desc), roleId: 'system_user', arguments: [], subCommands: [] };
}

/** `GET …/commands/autocomplete_suggestions` answer → rows (Go field names, tolerant). */
export function decodeSuggestions(raw: unknown): CommandSuggestion[] {
  return arr(raw)
    .map((x) => {
      const o = rec(x);
      return {
        complete: s(pick(o, 'Complete', 'complete')),
        suggestion: s(pick(o, 'Suggestion', 'suggestion')),
        hint: s(pick(o, 'Hint', 'hint')),
        description: s(pick(o, 'Description', 'description')),
      };
    })
    .filter((r) => r.complete !== '' || r.suggestion !== '' || r.hint !== '' || r.description !== '');
}

// MARK: - The walk (command_autocomplete.go, §36.2)

type Parse = { found: boolean; parsed: string; toBeParsed: string; rows: CommandSuggestion[]; needsServer: boolean };

const parse = (found: boolean, parsed: string, toBeParsed: string, rows: CommandSuggestion[] = [], needsServer = false): Parse => ({ found, parsed, toBeParsed, rows, needsServer });

const trimOneSpace = (s: string) => (s.startsWith(' ') ? s.slice(1) : s);

/** The server's role filter: `RoleID == role || role == system_admin` (so `RoleID: ""` is admins only). */
function visible(node: AutocompleteNode, isSystemAdmin: boolean): boolean {
  return isSystemAdmin || node.roleId === 'system_user';
}

/** A finished word (a space after it) is consumed; one still being typed ends the walk with `row`, if any. */
function consumeWord(parsed: string, toBeParsed: string, row: CommandSuggestion | null): Parse {
  const input = trimOneSpace(toBeParsed);
  const space = input.indexOf(' ');
  if (input !== '' && space !== -1) return parse(false, parsed + input.slice(0, space + 1), input.slice(space + 1));
  return parse(true, parsed + toBeParsed, '', row ? [row] : [], row !== null);
}

function parseNamed(arg: AutocompleteArg, parsed: string, toBeParsed: string): Parse {
  const input = trimOneSpace(toBeParsed);
  const named = '--' + arg.name;
  const row = (complete: string): CommandSuggestion => ({ complete, suggestion: named, hint: '', description: arg.helpText });
  if (input === '') return parse(true, parsed + toBeParsed, '', [row(parsed + toBeParsed + named + ' ')]);
  if (named.toLowerCase().startsWith(input.toLowerCase())) return parse(true, parsed + toBeParsed, '', [row(parsed + toBeParsed + named.slice(input.length) + ' ')]);
  if (!input.toLowerCase().startsWith(named.toLowerCase() + ' ')) return parse(false, parsed + toBeParsed, '');
  if (input.toLowerCase() === named.toLowerCase() + ' ') return parse(false, parsed + named + ' ', ' ');
  return parse(false, parsed + named + ' ', input.slice(named.length + 1));
}

function parseText(arg: AutocompleteArg, parsed: string, toBeParsed: string): Parse {
  const input = trimOneSpace(toBeParsed);
  const hintRow = () => parse(true, parsed + toBeParsed, '', [{ complete: parsed + toBeParsed, suggestion: '', hint: arg.hint, description: arg.helpText }]);
  if (input === '') return hintRow();
  if (input[0] === '"') {
    const second = input.slice(1).indexOf('"');
    if (second === -1) return hintRow(); // an open quote: still being typed
    let offset = 2;
    if (input.length > second + 2 && input[second + 2] === ' ') offset++;
    return parse(false, parsed + input.slice(0, second + offset), input.slice(second + offset));
  }
  const space = input.indexOf(' ');
  if (space === -1) return hintRow();
  return parse(false, parsed + input.slice(0, space + 1), input.slice(space + 1));
}

/** `parseListItems`: an item followed by a space is consumed (longest wins); else every item the text prefixes. */
function parseListItems(items: readonly AutocompleteListItem[], parsed: string, toBeParsed: string): Parse {
  const input = trimOneSpace(toBeParsed);
  let maxPrefix = '';
  for (const it of items) {
    if (input.toLowerCase().startsWith(it.item.toLowerCase() + ' ') && maxPrefix.length < it.item.length + 1) maxPrefix = it.item + ' ';
  }
  if (maxPrefix !== '') return parse(false, parsed + input.slice(0, maxPrefix.length), input.slice(maxPrefix.length));
  const rows = items
    .filter((it) => it.item.toLowerCase().startsWith(input.toLowerCase()))
    .map((it) => ({ complete: parsed + it.item, suggestion: it.item, hint: it.hint, description: it.helpText }));
  return parse(true, parsed + toBeParsed, '', rows);
}

function oneArgument(arg: AutocompleteArg, parsed: string, toBeParsed: string): Parse {
  if (arg.name !== '') {
    const named = parseNamed(arg, parsed, toBeParsed);
    if (named.found) return named;
    if (named.toBeParsed === '') return { ...named, found: true };
    parsed = named.parsed;
    toBeParsed = named.toBeParsed === ' ' ? '' : named.toBeParsed;
  }
  switch (arg.type) {
    case 'TextInput':
      return parseText(arg, parsed, toBeParsed);
    case 'StaticList':
      return parseListItems(arg.items, parsed, toBeParsed);
    case 'DynamicList':
      // Only the server can fetch the plugin's list: a placeholder row and `needsServer` (step 7).
      return consumeWord(parsed, toBeParsed, { complete: parsed + toBeParsed, suggestion: '', hint: '', description: arg.helpText });
    case 'Unknown':
      return consumeWord(parsed, toBeParsed, null);
  }
}

function parseArguments(args: readonly AutocompleteArg[], parsed: string, toBeParsed: string): Parse {
  if (args.length === 0) return parse(false, parsed, toBeParsed);
  const [first, ...rest] = args;
  if (first.required) {
    const r = oneArgument(first, parsed, toBeParsed);
    return r.found ? r : parseArguments(rest, r.parsed, r.toBeParsed);
  }
  // An optional argument may or may not have been typed: both readings, rows combined.
  const rows: CommandSuggestion[] = [];
  let needsServer = false;
  let withOpt = oneArgument(first, parsed, toBeParsed);
  if (withOpt.found) {
    rows.push(...withOpt.rows);
    needsServer ||= withOpt.needsServer;
  } else {
    const restAfter = parseArguments(rest, withOpt.parsed, withOpt.toBeParsed);
    if (restAfter.found) {
      rows.push(...restAfter.rows);
      needsServer ||= restAfter.needsServer;
    }
    withOpt = restAfter;
  }
  const without = parseArguments(rest, parsed, toBeParsed);
  if (without.found) {
    rows.push(...without.rows);
    needsServer ||= without.needsServer;
  }
  if (withOpt.found || without.found) return parse(true, parsed + toBeParsed, '', rows, needsServer);
  if (withOpt.parsed !== parsed && withOpt.toBeParsed !== toBeParsed) return { ...withOpt, rows, needsServer };
  return { ...without, rows, needsServer };
}

function walk(nodes: readonly AutocompleteNode[], parsed: string, toBeParsed: string, isSystemAdmin: boolean): CommandSuggestions {
  const space = toBeParsed.indexOf(' ');
  if (space === -1) {
    const word = toBeParsed.toLowerCase();
    return {
      // The first level is §15's strip, unchanged: no role filter there (§36.2).
      rows: nodes
        .filter((n) => n.trigger.toLowerCase().startsWith(word) && (parsed === '' || visible(n, isSystemAdmin)))
        .map((n) => ({ complete: parsed + n.trigger, suggestion: n.trigger, hint: n.hint, description: n.helpText })),
      needsServer: false,
    };
  }
  const rows: CommandSuggestion[] = [];
  let needsServer = false;
  const word = toBeParsed.slice(0, space).toLowerCase();
  for (const n of nodes) {
    if (n.trigger.toLowerCase() !== word || (parsed !== '' && !visible(n, isSystemAdmin))) continue;
    const rest = toBeParsed.slice(space + 1);
    const done = parsed + toBeParsed.slice(0, space + 1);
    const r = n.arguments.length === 0 ? { ...walk(n.subCommands, done, rest, isSystemAdmin), found: true } : parseArguments(n.arguments, done, rest);
    if (r.found) {
      rows.push(...r.rows);
      needsServer ||= r.needsServer;
    }
  }
  return { rows, needsServer };
}

/** The tree's commands sorted the server's way (by lower-cased trigger). */
export function sortNodes(nodes: readonly AutocompleteNode[]): AutocompleteNode[] {
  return [...nodes].sort((a, b) => {
    const x = a.trigger.toLowerCase();
    const y = b.trigger.toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  });
}

/**
 * The walk for the composer `text` (leading "/" included) — the rows `autocomplete_suggestions`
 * would answer, plus whether only the server can finish them. `null` = not a command line.
 */
export function rawSuggestions(nodes: readonly AutocompleteNode[], text: string, isSystemAdmin: boolean): CommandSuggestions | null {
  if (!text.startsWith('/') || text.startsWith('//') || text.includes('\n')) return null;
  return walk(sortNodes(nodes), '', text.slice(1), isSystemAdmin);
}

/** How many rows the first level shows (§15's strip cap). */
export const TOP_LEVEL_LIMIT = 6;
/** Below the first level: the walk's rows in the server's order, at most this many (§36.2). */
export const ROW_LIMIT = 25;

/**
 * What the strip SHOWS for a set of rows (local walk or the server's answer), §36.2:
 *  - when the line IS one of the choices (a takeable row's `complete` equals it) the strip is
 *    empty — §15's "a trigger typed out in full needs no strip" at every level: Enter must RUN
 *    "/call join", not complete it;
 *  - duplicates (same `complete` + `suggestion`) collapse;
 *  - the first level (no space typed yet) keeps §15's cap of 6, deeper levels at most 25.
 */
export function present(rows: readonly CommandSuggestion[], text: string): CommandSuggestion[] {
  const typed = text.replace(/^\//, '');
  const lower = typed.toLowerCase();
  if (rows.some((r) => r.suggestion !== '' && r.complete.toLowerCase() === lower)) return [];
  const seen = new Set<string>();
  const out: CommandSuggestion[] = [];
  for (const r of rows) {
    const key = r.complete + '\u0000' + r.suggestion;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return typed.includes(' ') ? out.slice(0, ROW_LIMIT) : out.filter((r) => r.suggestion !== '').slice(0, TOP_LEVEL_LIMIT);
}

/** A row the reader can take (a hint row is only read). */
export function isPickable(row: CommandSuggestion): boolean {
  return row.suggestion !== '';
}

/** The first takeable row, or -1 — the strip's initial highlight (hint rows are skipped). */
export function firstPickable(rows: readonly CommandSuggestion[]): number {
  return rows.findIndex(isPickable);
}

/** The next takeable row from `from` going `delta` (±1), wrapping; -1 when none can be taken. */
export function movePickable(rows: readonly CommandSuggestion[], from: number, delta: number): number {
  const n = rows.length;
  if (n === 0 || !rows.some(isPickable)) return -1;
  let i = from;
  for (let step = 0; step < n; step++) {
    i = (((i + delta) % n) + n) % n;
    if (isPickable(rows[i])) return i;
  }
  return -1;
}

/**
 * Taking a row: the composer becomes "/" + `complete`, plus one space unless it already ends with
 * one (webapp handleCompleteWord); the caret goes to the end (§36.2).
 */
export function apply(row: CommandSuggestion): string {
  return '/' + row.complete + (row.complete.endsWith(' ') ? '' : ' ');
}

/** A row's title: the offered word; a hint row reads its `hint`, or its description when that is empty. */
export function rowTitle(row: CommandSuggestion): string {
  return row.suggestion !== '' ? row.suggestion : row.hint !== '' ? row.hint : row.description;
}
